// THE ALBUM'S TITLE — ONE STORED VALUE, ONE MEANING, ONE WAY TO WRITE IT.
//
// ⚠️ ONE FILE, SHIPPED TWICE. worker/src/lib/titleSource.ts and
// web/src/lib/titleSource.ts must be byte for byte identical, and this file
// imports nothing so both packages can load it. Guard: titleSource.test.ts.
// (Not albumTitle.ts — that one DETECTS « change the title » in a chat message.)
//
// ─── OWNER, 2026-10-06 ──────────────────────────────────────────────────────
//
// « the title is stored in 5 places. » « One meaning each, read by everything:
// the agent, the reminders, the pipeline, the dashboard. »
//
// The same day #WA-269649 had « The Story of Najat » on the front and the
// spine, while `destination` still said « anniversaire 7 ans ». The chat's
// title change had been writing the title INTO `destination` — which is also
// how the caption writer learns the occasion (captionWriter.occasionOf) and how
// the layout picks its template family (ai.ts deriveCategorySlug). One column,
// two meanings: renaming the album erased what the album is about.
//
// ─── WHAT EACH PLACE MEANS FROM NOW ON ──────────────────────────────────────
//
//   album_sessions.cover_destination_text  THE TITLE — the words printed on
//                                          the front. The print reads only this.
//   album_sessions.title_declined          THE « NO TITLE » DECISION — he said
//                                          he wants no words on the cover.
//   album_sessions.destination             THE OCCASION — what he said the album
//                                          is about when he ordered (« anniversaire
//                                          7 ans », « Bali 2026 »). Read for the
//                                          caption tone, the layout family, the
//                                          cover matcher, photo-batch matching,
//                                          and as the DEFAULT title. A title
//                                          change never writes it.
//
//   Copies, written only together with the title (here + lib/titleWrite.ts):
//   cover_customizations.spineConfig.text  the spine — the same title, edge on
//   orders.destination_1..3                the order's copy — the admin list,
//                                          the reminder templates, the printer
//                                          sheet and the parcel label read it
//
// Everything else is a reader: it asks `albumTitle` / `albumName` below.

/** The two columns that ARE the title. Nothing else is. */
export interface TitleColumns {
  cover_destination_text?: string | null;
  title_declined?: boolean | null;
}

/** …plus the order-time words, for the screens that need a name or a default. */
export interface TitleAndOccasion extends TitleColumns {
  destination?: string | null;
}

/**
 * The title, as one of three answers:
 *   set  — words to print (exactly as stored: line breaks are the customer's)
 *   none — he decided: no words on the cover
 *   owed — nobody has told us yet
 *
 * ⚠️ WORDS WIN OVER A STALE « NO TITLE », because the print draws the words.
 * `titlePatch` never leaves both; this only keeps the answer honest if a row
 * written by hand ever does.
 */
export type AlbumTitle =
  | { kind: 'set'; text: string }
  | { kind: 'none' }
  | { kind: 'owed' };

export function albumTitle(row: TitleColumns | null | undefined): AlbumTitle {
  const raw = typeof row?.cover_destination_text === 'string' ? row.cover_destination_text : '';
  if (raw.trim()) return { kind: 'set', text: raw };
  if (row?.title_declined === true) return { kind: 'none' };
  return { kind: 'owed' };
}

/** The words to print, or null (no title, or none yet). */
export function titleText(row: TitleColumns | null | undefined): string | null {
  const t = albumTitle(row);
  return t.kind === 'set' ? t.text : null;
}

/**
 * Is the title question answered? Words to print OR a recorded decision to
 * print none (owner, 2026-09-21: « if the client said he doesn't want a title,
 * it's okay »). The scoreboard (should we ask?) and the render gate (may it be
 * built?) both ask this.
 */
export function titleSettled(row: TitleColumns | null | undefined): boolean {
  return albumTitle(row).kind !== 'owed';
}

/** A title on ONE line, for a list, a message or the order's copy. */
export function titleLine(s: string | null | undefined): string {
  return String(s ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * What a screen or a message CALLS the album: its title on one line — or,
 * while it has no title (owed or declined), the words it was ordered with.
 * A name for finding the album, never a value to print.
 */
export function albumName(row: TitleAndOccasion | null | undefined): string | null {
  const t = albumTitle(row);
  if (t.kind === 'set') return titleLine(t.text) || null;
  return titleLine(row?.destination) || null;
}

/**
 * What a title box shows before the customer types: the stored title; while
 * none is stored, the words typed on the order form (`destination`, else the
 * order's own copy); and EMPTY when he chose no title — never the order words
 * over his decision.
 */
export function titlePrefill(row: TitleAndOccasion | null | undefined, orderWords?: string | null): string {
  const t = albumTitle(row);
  if (t.kind === 'set') return t.text;
  if (t.kind === 'none') return '';
  return String(row?.destination ?? '').trim() || String(orderWords ?? '').trim();
}

// ─── WRITING IT ──────────────────────────────────────────────────────────────

/** Machine strings a customer never meant as words on a book: a shared pin's
 *  Plus Code (#1203 « H9Q9+3P7 Casablanca »), raw coordinates, a link. */
const PLUS_CODE = /\b[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}\b/i;
const COORDS = /-?\d{1,3}\.\d{3,}\s*,\s*-?\d{1,3}\.\d{3,}/;
const LINKISH = /https?:\/\/|www\.|\.(?:com|ma|fr|net|org)\b/i;

export function isMachineString(s: string | null | undefined): boolean {
  const t = String(s ?? '');
  return PLUS_CODE.test(t) || COORDS.test(t) || LINKISH.test(t);
}

/** The order-time words as a DEFAULT title, or null when they cannot be one. */
export function defaultTitleFrom(words: string | null | undefined): string | null {
  const t = String(words ?? '').trim();
  if (!t || isMachineString(t)) return null;
  return t;
}

export interface TitlePatch {
  cover_destination_text: string | null;
  title_declined: boolean;
  cover_customizations?: Record<string, unknown>;
}

/**
 * THE album_sessions patch for a title change. `next` is the new title; null
 * or blank means « no title » (the decision, recorded — and the printed words
 * cleared, because the print reads the words, not the flag).
 *
 * The spine follows: it is the same title read edge-on (owner: « the title
 * needs to match with the spine text as well »). Only an EXISTING spine is
 * touched — a lone `{ text }` would load over the editor's spine defaults and
 * draw on no background; the picker builds the full spine when the cover is
 * chosen, from this title.
 *
 * ⚠️ NEVER `destination`. That column is the occasion.
 */
export function titlePatch(next: string | null | undefined, prevCustomizations: unknown): TitlePatch {
  const text = String(next ?? '').trim();
  const patch: TitlePatch = { cover_destination_text: text || null, title_declined: !text };
  const prev = (prevCustomizations && typeof prevCustomizations === 'object' && !Array.isArray(prevCustomizations))
    ? prevCustomizations as Record<string, unknown> : null;
  const spine = prev?.spineConfig;
  if (prev && spine && typeof spine === 'object' && !Array.isArray(spine)) {
    patch.cover_customizations = { ...prev, spineConfig: { ...(spine as Record<string, unknown>), text } };
  }
  return patch;
}

/**
 * The DEFAULT title — the words he ordered with — and only when the title is
 * still owed. A default never overwrites a title, and never a « no title ».
 * Null when there is nothing to do.
 */
export function defaultTitlePatch(row: TitleAndOccasion | null | undefined, prevCustomizations: unknown): TitlePatch | null {
  if (albumTitle(row).kind !== 'owed') return null;
  const words = defaultTitleFrom(row?.destination);
  return words ? titlePatch(words, prevCustomizations) : null;
}

/** For an album being CREATED from what was typed on the order: its first title. */
export function seedTitleColumns(words: string | null | undefined): { cover_destination_text?: string } {
  const t = defaultTitleFrom(words);
  return t ? { cover_destination_text: t } : {};
}

/** The order's copy of album N's title. */
export function orderTitleColumn(albumIndex: number | null | undefined): 'destination_1' | 'destination_2' | 'destination_3' | null {
  const n = Number(albumIndex);
  return n === 1 ? 'destination_1' : n === 2 ? 'destination_2' : n === 3 ? 'destination_3' : null;
}
